import React, { useState, useMemo, useCallback } from "react";
import { Controller, Control } from "react-hook-form";
import { Input, Tooltip } from "@heroui/react";
import { useQuery } from "@tanstack/react-query";
import useConfigFieldsValues from "@/hooks/useConfigFieldsValues";
import { useTemplateLoader } from "@/hooks/useTemplateLoader";
import { useUseCases } from "@/context/UseCasesProvider";
import { pluralizeUnit } from "@/utils/pluralization";
import { BsInfoCircleFill } from "react-icons/bs";

export type UnitType = "atom" | "moq" | "delivery" | "production" | "base" | "piece";

export interface Product {
  product_id: string;
  product_atom_moq: string;
  product_moq_quantity: string;
  product_atom_quantity: string;
  product_delivery_quantity?: string;
  product_production_quantity?: string;
  product_logistic_quantity?: string;
  product_base_quantity?: string;
  product_piece_quantity?: string;
  product_atom_unit: string;
  product_moq_unit: string;
  product_delivery_unit: string;
  product_production_unit: string;
  product_logistic_unit: string;
  product_base_unit?: string;
  product_piece_unit?: string;
}

export interface ProductQuantityProps {
  control: Control<any>;
  product: {
    productID: string;
    index: number;
    productQuantity: number;
    productQuantityStatus: number;
    quantityIdCorrected: boolean;
    packingCustomerIdCorrected: boolean;
    productPacking: string;
    productPackingCustomer: string;
    productPackingCustomerStatus: number;
  };
  isDataReadOnlyOperators: boolean;
  getValues: (name: string) => any;
  handleQuantityChange: (index: number, value: number) => void;
  handlePackingCustomerChange?: (index: number, value: string) => void;
  setModifiedValues: React.Dispatch<React.SetStateAction<Record<number, boolean>>>;
  setValue: (name: string, value: any) => void;
  unit: UnitType;
  customTabIndex?: number;
}

export interface ProductQuantityData {
  displayValue: number;
  moqValue: number;
  productQuantities: {
    atom: number;
    moq: number;
    delivery: number;
    production: number;
    logistic: number;
    base?: number;
    piece?: number;
  };
  productUnits: {
    atom: string;
    moq: string;
    delivery: string;
    production: string;
    logistic: string;
    base?: string;
    piece?: string;
  };
  calculations: {
    quantityInAtom: number;
    quantityInMoq: number;
    quantityInDelivery: number;
    quantityInProduction: number;
    quantityInLogistic: number;
  };
  validation: {
    borderColorClass: string;
    textColorClass: string;
    isValid: boolean;
  };
  config: {
    minValue?: number;
    stepValue: number;
    showDisplayText: boolean;
    showTooltip: boolean;
    displayText: string;
  };
}

export interface ProductQuantityHandlers {
  onChange: (value: number) => void;
  onStepperChange: () => void;
}

export interface ProductQuantityComponents {
  generateTooltipContent: (params: TooltipContentParams) => React.ReactElement;
  generateDisplayText: (params: any) => string;
}

export interface TemplateProps extends ProductQuantityProps {
  data: ProductQuantityData;
  handlers: ProductQuantityHandlers;
  components: ProductQuantityComponents;
  settingsInterface: SettingsInterface | null;
}

export interface TooltipContentParams {
  displayValue: number;
  unit: UnitType;
  productQuantities: {
    atom: number;
    moq: number;
    delivery: number;
    production: number;
    logistic: number;
    base?: number;
    piece?: number;
  };
  productUnits: {
    atom: string;
    moq: string;
    delivery: string;
    production: string;
    logistic: string;
    base?: string;
    piece?: string;
  };
  roundToDecimals: (num: number, decimals?: number) => number;
}

export interface SettingsInterface {
  settings: any;
}

// ===================================
// HOOK
// ===================================

const useProductQuantityLogic = (props: ProductQuantityProps) => {
  const settingsInterface = useUseCases() as SettingsInterface;
  const productQuantitySettings = settingsInterface?.settings?.display?.product_quantity;
  const { quantityMoqVerification } = useConfigFieldsValues();
  const { data: productsData } = useQuery<Product[]>({ queryKey: ["allProducts"] });

  const [isStepperChange, setIsStepperChange] = useState(false);
  // Données produit
  const currentProductData = useMemo(
    () => productsData?.find((item) => item.product_id === props.product?.productID),
    [productsData, props.product?.productID]
  );

  const productQuantities = useMemo(
    () => ({
      atom: currentProductData ? Number.parseFloat(currentProductData.product_atom_quantity) : 1,
      moq: currentProductData ? Number.parseFloat(currentProductData.product_moq_quantity) : 1,
      delivery: currentProductData
        ? Number.parseFloat(currentProductData.product_delivery_quantity ?? "1")
        : 1,
      production: currentProductData
        ? Number.parseFloat(currentProductData.product_production_quantity ?? "1")
        : 1,
      logistic: currentProductData
        ? Number.parseFloat(currentProductData.product_logistic_quantity ?? "1")
        : 1,
      base: currentProductData?.product_base_quantity ? Number.parseFloat(currentProductData.product_base_quantity) : undefined,
      piece: currentProductData?.product_piece_quantity ? Number.parseFloat(currentProductData.product_piece_quantity) : undefined,
    }),
    [currentProductData]
  );

  const productUnits = useMemo(
    () => ({
      atom: currentProductData?.product_atom_unit || "UNITE",
      moq: currentProductData?.product_moq_unit || "MOQ",
      delivery: currentProductData?.product_delivery_unit || (currentProductData?.product_delivery_quantity && currentProductData.product_delivery_quantity !== "" ? "COLIS" : ""),
      production: currentProductData?.product_production_unit || "KG",
      logistic: currentProductData?.product_logistic_unit || "PALETTE",
      base: currentProductData?.product_base_unit || undefined,
      piece: currentProductData?.product_piece_unit || undefined,
    }),
    [currentProductData]
  );

  const moqValue = useMemo(() => {
    if (!props.product) return 0;
    return typeof props.getValues(`productQuantity-${props.product.index}`) !== "undefined"
      ? props.getValues(`productQuantity-${props.product.index}`)
      : props.product.productQuantity;
  }, [props.getValues, props.product]);

  const displayValue = useMemo(() => {
    if (!props.product) return 0;
    const result = calculateDisplayValue(
      moqValue,
      props.unit,
      productQuantities.atom,
      productQuantities.delivery,
      productQuantities.piece
    );
    return result;
  }, [moqValue, props.unit, productQuantities.atom, productQuantities.delivery, props.product]);

  const calculations = useMemo(
    () => calculateAllQuantities(displayValue, props.unit, productQuantities),
    [displayValue, props.unit, productQuantities]
  );

  const validation = useMemo(() => {
    if (!props.product) {
      return {
        borderColorClass: "border-red-500",
        textColorClass: "text-red-500",
        isValid: false,
      };
    }

    let borderColorClass: string;
    if (props.product.productQuantityStatus < 25) {
      borderColorClass = "border-red-500";
    } else if (props.product.productQuantityStatus < 75) {
      borderColorClass = "border-orange-300";
    } else {
      borderColorClass = "border-default-200";
    }

    let textColorClass: string;
    if (props.unit === "atom" && quantityMoqVerification === true) {
      const isIntegerQuantity = Number.isInteger(
        displayValue / (productQuantities.atom * productQuantities.moq)
      );
      textColorClass = isIntegerQuantity ? "text-vistagreen/70" : "text-red-500";
    } else {
      textColorClass = "text-vistagreen/70";
    }

    return {
      borderColorClass,
      textColorClass,
      isValid: true,
    };
  }, [
    props.product,
    props.unit,
    quantityMoqVerification,
    displayValue,
    productQuantities,
  ]);
  
  const config = useMemo(
    () => ({
      minValue: calculateMinValue(
        productQuantitySettings,
        props.unit,
        quantityMoqVerification === true,
        productQuantities
      ),
      stepValue: calculateStepValue(
        productQuantitySettings,
        props.unit,
        quantityMoqVerification === true,
        productQuantities
      ),
      showTooltip:
        settingsInterface?.settings?.display?.product_quantity?.display_conversion || false,
      showDisplayText:
        (productQuantitySettings?.atom || productQuantitySettings?.delivery) &&
        !isNaN(displayValue) &&
        displayValue > 0,
      displayText: generateDisplayText({
        displayValue,
        unit: props.unit,
        productQuantities: {
          atom: productQuantities.atom,
          moq: productQuantities.moq,
          delivery: productQuantities.delivery,
        },
        productUnits: {
          atom: productUnits.atom,
          moq: productUnits.moq,
        },
        settingsInterface,
        roundToDecimals,
      }),
    }),
    [
      productQuantitySettings,
      props.unit,
      quantityMoqVerification,
      productQuantities,
      displayValue,
      productUnits,
      settingsInterface,
    ]
  );

  // Handlers
  const handleChange = useCallback(
    (inputValue: number) => {
      if (!props.product) return;
      
      let processedValue = inputValue;
      if (
        isStepperChange &&
        props.unit === "atom" &&
        quantityMoqVerification === true &&
        !productQuantitySettings?.authorize_unpacking
      ) {
        const step = productQuantities.atom * productQuantities.moq;
        processedValue = Math.round(inputValue / step) * step;
      }
      const newValue = roundToDecimals(
        calculateNewValue(
          processedValue,
          props.unit,
          productQuantities.atom,
          productQuantities.delivery,
          productQuantities.moq,
          productQuantities.piece
        ),
        3
      );
      props.setValue(`productQuantity-${props.product.index}`, newValue);
      props.setModifiedValues((prev) => ({ ...prev, [props.product.index]: true }));
      props.handleQuantityChange(props.product.index, newValue);
      setIsStepperChange(false);
    },
    [
      props,
      productQuantities,
      isStepperChange,
      quantityMoqVerification,
      productQuantitySettings?.authorize_unpacking,
    ]
  );

  const handleStepperChange = useCallback(() => {
    setIsStepperChange(true);
  }, []);

  const components: ProductQuantityComponents = useMemo(
    () => ({
      generateTooltipContent: (params: TooltipContentParams) => generateTooltipContent(params),
      generateDisplayText: (params: GenerateDisplayTextParams) => generateDisplayText(params),
    }),
    []
  );

  if (!settingsInterface || !props.product) {
    return {
      data: {
        displayValue: 0,
        moqValue: 0,
        productQuantities: { atom: 1, moq: 1, delivery: 1, logistic: 1 },
        productUnits: { atom: "UNITE", moq: "MOQ", delivery: "COLIS", logistic: "PALETTE" },
        calculations: { quantityInAtom: 0, quantityInMoq: 0, quantityInDelivery: 0, quantityInLogistic: 0 },
        validation: { borderColorClass: "border-default-200", textColorClass: "text-gray-500", isValid: false },
        config: { minValue: 1, stepValue: 1, showDisplayText: false, showTooltip: false, displayText: "" },
      },
      handlers: {
        onChange: () => {},
        onStepperChange: () => {},
      },
      components: {
        generateTooltipContent: () => <div>Chargement...</div>,
        generateDisplayText: () => "",
      },
      isReady: false,
    };
  }

  const templateData: ProductQuantityData = {
    displayValue,
    moqValue,
    productQuantities,
    productUnits,
    calculations,
    validation,
    config,
  };

  const handlers: ProductQuantityHandlers = {
    onChange: handleChange,
    onStepperChange: handleStepperChange,
  };

  return {
    data: templateData,
    handlers,
    components,
    isReady: true,
  };
};

// ===================================
// TEMPLATE PAR DÉFAUT
// ===================================

export const DefaultTemplate: React.FC<TemplateProps> = ({
  data,
  handlers,
  components,
  control,
  product,
  isDataReadOnlyOperators,
  customTabIndex,
  unit,
}) => {
  if (!data || !handlers || !components || !control || !product) {
    return (
      <div className="text-red-500 text-sm">Erreur: Props manquantes dans DefaultTemplate</div>
    );
  }

  return (
    <div className="relative w-[150px]">
      <Controller
        name={`productQuantity-${product.index}`}
        control={control}
        render={({ field }) => (
          <>
            <Input
              {...field}
              type="number"
              min={data.config.minValue}
              step={data.config.stepValue}
              readOnly={isDataReadOnlyOperators}
              classNames={{
                base: [
                  `min-w-[150px]`,
                ],
                inputWrapper: [
                  `h-8 ${data.validation.borderColorClass}`,
                  "group-data-[focus-visible=true]:outline-none",
                  "group-data-[focus-visible=true]:ring-0",
                  "group-data-[focus-visible=true]:ring-offset-0",
                  "group-data-[focus-visible=true]:!border-vistagreen",
                  "group-data-[focus-visible=true]:!border-2",
                  "group-data-[focus-visible=true]:!border-solid",
                  "group-data-[hover=true]:outline-none",
                  "group-data-[hover=true]:ring-0",
                  "group-data-[hover=true]:ring-offset-0",
                  "group-data-[hover=true]:!border-vistagreen",
                  "group-data-[hover=true]:!border-2",
                  "group-data-[hover=true]:!border-solid",
                  "group-data-[hover=true]:!bg-default-100",
                ],
              }}
              onFocus={(e) => {
                e.target.select();
              }}
              onKeyDown={(e) => {
                if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                  handlers.onStepperChange();
                }
              }}
              onMouseDown={(e) => {
                const target = e.target as HTMLElement;
                if (target.closest("button") || target.getAttribute("role") === "spinbutton") {
                  handlers.onStepperChange();
                }
              }}
              onChange={(e) => {
                const value = e.target.value;

                if (value !== "" && !Number.isNaN(Number.parseFloat(value)) && Number.parseFloat(value) > 0) {
                  const inputValue = Number.parseFloat(value);
                  handlers.onChange(inputValue);
                } else if (value === "") {
                  field.onChange("");
                }
              }}
              value={String(data.displayValue)}
              variant={
                product.productQuantityStatus < 75 && !product.quantityIdCorrected
                  ? "bordered"
                  : "flat"
              }
              tabIndex={customTabIndex}
            />

            {data.config.showDisplayText && (
              <div className="absolute left-0 w-[200px] z-[9]">
                <div
                  className={`text-[11px] font-bold -mb-3 ${data.validation.textColorClass} flex items-center`}
                >
                  {data.config.showTooltip && (
                    <Tooltip
                      content={components.generateTooltipContent({
                        displayValue: data.displayValue,
                        unit: unit,
                        productQuantities: data.productQuantities,
                        productUnits: data.productUnits,
                        roundToDecimals,
                      })}
                      showArrow={true}
                      placement="left"
                      delay={0}
                      closeDelay={0}
                      classNames={{
                        base: ["before:bg-vistagreen"],
                        content: ["bg-vistagreen p-0 rounded-lg"],
                      }}
                      offset={20}
                    >
                      <div className="mr-1">
                        <BsInfoCircleFill className="text-gray-400 h-6" />
                      </div>
                    </Tooltip>
                  )}
                  {data.config.displayText}
                </div>
              </div>
            )}
          </>
        )}
      />
    </div>
  );
};

// ===================================
// COMPOSANT PRINCIPAL
// ===================================

const ProductQuantity: React.FC<ProductQuantityProps> = (props) => {

  const logicResult = useProductQuantityLogic(props);
  const settingsInterface = useUseCases();
  const templateName = settingsInterface?.settings?.display?.product_quantity?.template || 'default';
  const Template = useTemplateLoader("ProductQuantity", templateName, DefaultTemplate, true);
  
  if (!logicResult?.isReady) {
    return <div className="text-gray-500 text-sm">Initialisation en cours...</div>;
  }

  const { data, handlers, components } = logicResult;

  if (!data || !handlers || !components) {
    return <div className="text-gray-500 text-sm">Chargement des données...</div>;
  }

  return <Template {...props} data={data} handlers={handlers} components={components} settingsInterface={settingsInterface} />;
};

export default ProductQuantity;

// ===================================
// FONCTIONS UTILITAIRES
// ===================================

const roundToDecimals = (num: number, decimals: number = 3): number =>
  Math.round(num * Math.pow(10, decimals)) / Math.pow(10, decimals);

const calculateDisplayValue = (
  moqValue: number,
  unit: UnitType,
  productAtomQuantity: number,
  productDeliveryQuantity: number,
  productPieceQuantity: number | undefined,
): number => {
  let displayValue: number;
  switch (unit) {
    case "atom":
      displayValue = moqValue * productAtomQuantity;
      break;
    case "delivery":
      displayValue = moqValue / productDeliveryQuantity;
      break;
    case "piece":
      displayValue = moqValue; // cas unité iso piece et moq
      break;
    case "base":
      displayValue = moqValue * (productPieceQuantity ?? 1); // cas unité iso piece et moq
      break;
    default: // moq
      displayValue = moqValue;
      break;
  }
  return roundToDecimals(displayValue, 3);
};

const calculateNewValue = (
  inputValue: number,
  unit: UnitType,
  productAtomQuantity: number,
  productDeliveryQuantity: number,
  productProductionQuantity: number,
  productPieceQuantity: number | undefined,

): number => {
  switch (unit) {
    case "atom":
      return inputValue / productAtomQuantity;
    case "delivery":
      return inputValue * productDeliveryQuantity;
    case "production":
      return inputValue * productProductionQuantity;
    case "base":
      return inputValue / (productPieceQuantity ?? 1);
    case "piece":
      return inputValue;
    default: // moq
      return inputValue;
  }
};

const calculateAllQuantities = (displayValue: number, unit: UnitType, productQuantities: any) => {
  const {
    atom: productAtomQuantity,
    delivery: productDeliveryQuantity,
    logistic: productLogisticQuantity,
  } = productQuantities;

  const smartRound = (value: number, threshold: number = 0.02): number => {
    const rounded = Math.round(value);
    return Math.abs(value - rounded) < threshold ? rounded : roundToDecimals(value, 3);
  };

  let quantityInAtom: number;
  let quantityInMoq: number;
  let quantityInDelivery: number;
  let quantityInProduction: number;
  let quantityInLogistic: number;

  if (unit === "atom") {
    quantityInAtom = displayValue;
    quantityInMoq = smartRound(displayValue / productAtomQuantity);
    quantityInDelivery = smartRound(displayValue / productAtomQuantity / productDeliveryQuantity);
  } else if (unit === "moq") {
    quantityInMoq = displayValue;
    quantityInAtom = smartRound(displayValue * productAtomQuantity);
    quantityInDelivery = smartRound(displayValue / productDeliveryQuantity);
  } else {
    quantityInDelivery = displayValue;
    quantityInAtom = smartRound(displayValue * productDeliveryQuantity * productAtomQuantity);
    quantityInMoq = smartRound(displayValue * productDeliveryQuantity);
  }

  quantityInProduction = quantityInMoq;

  quantityInLogistic =
    productLogisticQuantity && productLogisticQuantity > 0
      ? roundToDecimals(quantityInMoq / productLogisticQuantity, 3)
      : 0;

  return {
    quantityInAtom,
    quantityInMoq,
    quantityInDelivery,
    quantityInProduction,
    quantityInLogistic,
  };
};

const calculateMinValue = (
  productQuantitySettings: any,
  unit: UnitType,
  quantityMoqVerification: boolean,
  productQuantities: any
): number | undefined => {
  if (productQuantitySettings?.authorize_unpacking || unit === "delivery") {
    return 1;
  }
  if (quantityMoqVerification && unit === "atom") {
    return productQuantities.atom * productQuantities.moq;
  }
  if (quantityMoqVerification) {
    return productQuantities.moq;
  }
  if (unit === "atom") {
    return 1;
  }
  return undefined;
};

const calculateStepValue = (
  productQuantitySettings: any,
  unit: UnitType,
  quantityMoqVerification: boolean,
  productQuantities: any
): number => {
  if (productQuantitySettings?.authorize_unpacking || unit === "delivery") {
    return 1;
  }
  if (quantityMoqVerification && unit === "atom") {
    return productQuantities.atom * productQuantities.moq;
  }
  if (unit === "atom") {
    return 1;
  }
  if (unit === "base") {
    return productQuantities.piece;
  }
  if (unit === "piece") {
    return 1;
  }
  if (quantityMoqVerification) {
    return productQuantities.moq;
  }
  return 1 / productQuantities.atom;
};

interface GenerateDisplayTextParams {
  displayValue: number;
  unit: UnitType;
  productQuantities: {
    atom: number;
    moq: number;
    delivery: number;
  };
  productUnits: {
    atom: string;
    moq: string;
  };
  settingsInterface: SettingsInterface | null;
  roundToDecimals: (num: number, decimals?: number) => number;
}

const generateDisplayText = (params: GenerateDisplayTextParams): string => {
  const { displayValue, unit, productQuantities, productUnits, settingsInterface, roundToDecimals } = params;
  if (!settingsInterface?.settings?.interface?.product_quantity) {
    return "";
  }

  const displayAtom = settingsInterface.settings.display.product_quantity.atom;
  const displayMoq = settingsInterface.settings.display.product_quantity.moq;

  const smartRound = (value: number, threshold: number = 0.02): number => {
    const rounded = Math.round(value);
    return Math.abs(value - rounded) < threshold ? rounded : roundToDecimals(value, 3);
  };

  let quantityInAtom: number;
  let quantityInMoq: number;

  if (unit === "atom") {
    quantityInAtom = displayValue;
    quantityInMoq = smartRound(displayValue / productQuantities.atom);
  } else if (unit === "moq") {
    quantityInMoq = displayValue;
    quantityInAtom = smartRound(displayValue * productQuantities.atom);
  } else {
    quantityInAtom = smartRound(displayValue * productQuantities.delivery * productQuantities.atom);
    quantityInMoq = smartRound(displayValue * productQuantities.delivery);
  }

  const availableUnits: { value: number; label: string }[] = [];

  switch (unit) {
    case "atom":
      if (displayMoq) {
        availableUnits.push({
          value: quantityInMoq,
          label: `${productUnits.moq} : ${quantityInMoq}  // ${productUnits.atom} :${quantityInAtom} `,
        });
      }
      break;

    case "moq":
    case "delivery":
      if (displayAtom) {
        availableUnits.push({
          value: quantityInAtom,
          label: `${productUnits.atom} : ${quantityInAtom} // ${productUnits.moq} : ${quantityInMoq}`,
        });
      }
      break;
  }

  return availableUnits.length > 0 ? availableUnits.map((unit) => unit.label).join(" // ") : "";
};

const generateTooltipContent = ({
  displayValue,
  unit,
  productQuantities,
  productUnits,
  roundToDecimals,
}: TooltipContentParams) => {
  const calculations = calculateAllQuantities(displayValue, unit, productQuantities);
  const { quantityInAtom, quantityInMoq, quantityInDelivery, quantityInLogistic } = calculations;
  const {
    atom: productAtomUnit,
    moq: productMoqUnit,
    delivery: productDeliveryUnit,
    logistic: productLogisticUnit,
  } = productUnits;
  const {
    atom: productAtomQuantity,
    moq: productMoqQuantity,
    delivery: productDeliveryQuantity,
    logistic: productLogisticQuantity,
  } = productQuantities;

  const deliveryQuantityPerLogistic =
    productLogisticQuantity && productDeliveryQuantity && productDeliveryQuantity !== 0
      ? roundToDecimals(productLogisticQuantity / productDeliveryQuantity, 3)
      : 0;

  const safeDisplayNumber = (value: number): string => {
    if (isNaN(value) || !isFinite(value)) return "0";
    return String(value);
  };

  return (
    <div className="mx-1 px-3 py-3 min-w-[200px] bg-vistagreen text-white rounded-lg">
      {/* Logistic */}
      {productLogisticUnit &&
        typeof productLogisticQuantity === "number" &&
        !isNaN(productLogisticQuantity) &&
        productLogisticQuantity > 0 && (
          <>
            <div>
              <div className="text-sm font-semibold text-white">
                {pluralizeUnit(productLogisticUnit, quantityInLogistic)} :{" "}
                {safeDisplayNumber(quantityInLogistic)}
              </div>
              <div className="text-xs text-white/60">
                {safeDisplayNumber(deliveryQuantityPerLogistic)}{" "}
                {pluralizeUnit(productDeliveryUnit, deliveryQuantityPerLogistic)} par{" "}
                {productLogisticUnit}
              </div>
            </div>
            <hr className="border-white/20 bg-white/20 my-2" />
          </>
        )}

      {/* Delivery */}
      <div className="mb-2">
        <div className="text-sm font-semibold text-white">
          {pluralizeUnit(productDeliveryUnit, quantityInDelivery)} : {quantityInDelivery}
        </div>
        <div className="text-xs text-white/60">
          {productDeliveryQuantity} {pluralizeUnit(productMoqUnit, productDeliveryQuantity)} par{" "}
          {productDeliveryUnit}
        </div>
      </div>

      <hr className="border-white/20 bg-white/20 my-2" />

      {/* MOQ */}
      <div className="mb-2">
        <div className="text-sm font-semibold text-white">
          {pluralizeUnit(productMoqUnit, quantityInMoq)} : {quantityInMoq}
        </div>
        <div className="text-xs text-white/60">Quantité minimum : {productMoqQuantity}</div>
      </div>

      <hr className="border-white/20 bg-white/20 my-2" />

      {/* Atom */}
      <div className="mb-2">
        <div className="text-sm font-semibold text-white">
          {pluralizeUnit(productAtomUnit, quantityInAtom)} : {quantityInAtom}
        </div>
        <div className="text-xs text-white/60">
          {productAtomQuantity} {pluralizeUnit(productAtomUnit, productAtomQuantity)} par{" "}
          {productMoqUnit}
        </div>
      </div>
    </div>
  );
};